import { useContext, useState } from 'react';
import './ProblemDetails.css';
import { AppInfoContext, UserContext } from '../../../../../App';
import { useQuery } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import FIELD_TYPE from '../reportproblem/components/selectOptions/FieldType';
import LocationComponent from '../reportproblem/components/selectOptions/LocationComponent';
import DMA from '../reportproblem/components/selectOptions/DMA';
import { formatInTimeZone } from 'date-fns-tz';
import ImageModal from '../../citizenreports/ImageModal';
import expandImageIcon from './images/expand.svg';
import noImageIcon from './images/no-image.svg';
import { useIsMobile } from '../reportproblem/ReportProblem';

export const getLabelByValue = (value, enumType) => {
  const field = Object.values(enumType).find((type) => type.value === value);
  return field ? field.label : 'Unknown';
};

export default function ProblemDetails({ setFormStep }) {
  const { appInformation } = useContext(AppInfoContext);
  const { userDetails } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [showImage, setShowImage] = useState(false);
  const isMobile = useIsMobile();
  const token = localStorage.getItem('token');

  const apiUrl = process.env.REACT_APP_BACKEND_API;
  const geoUrl = process.env.REACT_APP_GEOSERVER;

  const fetchProblemDetails = async () => {
    const response = await fetch(
      `${apiUrl}/problems/${appInformation.problemManagement.id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.json();
  };

  const { data } = useQuery(['problemDetails'], fetchProblemDetails, {
    onSuccess: (response) => {
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);
    },
  });

  return (
    <>
      {isLoading && (
        <div className="problem-details-spinner">
          <ClipLoader
            color={'gray'}
            loading={isLoading}
            size={60}
            data-testid="loader"
          />
        </div>
      )}

      {!isLoading &&
        (data?.image || data?.issue?.image ? (
          <div
            className="problem-details-image-desktop"
            style={{ position: 'fixed' }}
          >
            <img
              src={
                data.image
                  ? `${geoUrl}/static/problems/${data.image}`
                  : `${geoUrl}/static/issues/${data.issue.image}`
              }
              className="problem-details-image-preview"
              style={{
                right:
                  appInformation.statistics.problemModalActive &&
                  !appInformation.statistics.statisticsMinimized
                    ? '440px'
                    : '70px',
              }}
              alt=""
            />
            <div
              style={{ position: 'relative' }}
              className="problem-details-full-screen"
              onClick={() => setShowImage(true)}
            >
              <img
                src={expandImageIcon}
                style={{ height: '18px' }}
                alt="Icon"
              />
            </div>
          </div>
        ) : (
          <div className="problem-details-no-image problem-details-image-desktop">
            <img
              src={noImageIcon}
              className="problem-details-no-image-preview"
              alt=""
            />
          </div>
        ))}

      {!isLoading && (
        <div>
          <div
            className="problem-details-form-body"
            style={{
              height: appInformation.statistics.problemModalActive
                ? isMobile
                  ? '70vh'
                  : '360px'
                : '',
            }}
          >
            <div>
              <div>
                <label htmlFor="address" className="problem-details-label">
                  Adresa kvara
                </label>
                <input
                  disabled
                  type="text"
                  id="address"
                  value={data.address}
                  name="address"
                  maxLength="40"
                  className="problem-details-field"
                />
              </div>
              <div className="problem-details-date">
                <div>
                  <label
                    htmlFor="reportedDate"
                    className="problem-details-label"
                  >
                    Datum unosa
                  </label>
                  <input
                    type="text"
                    id="reportedDate"
                    name="reportedDate"
                    className="problem-details-date-field"
                    value={formatInTimeZone(
                      data.reportedAt,
                      'Europe/Paris',
                      'dd.MM.yyyy.'
                    )}
                    disabled
                  />
                </div>
                <div>
                  <label
                    htmlFor="reportedTime"
                    className="problem-details-label"
                  >
                    Vrijeme unosa
                  </label>
                  <input
                    type="text"
                    id="reportedTime"
                    name="reportedTime"
                    className="problem-details-date-field"
                    value={formatInTimeZone(
                      data.reportedAt,
                      'Europe/Paris',
                      'HH:mm'
                    )}
                    disabled
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fieldType" className="problem-details-label">
                  Vrsta terena
                </label>
                <input
                  disabled
                  type="text"
                  id="fieldType"
                  value={getLabelByValue(data.fieldType, FIELD_TYPE)}
                  name="fieldType"
                  className="problem-details-field"
                />
              </div>

              <div>
                <label
                  htmlFor="locationComponent"
                  className="problem-details-label"
                >
                  Pozicija
                </label>
                <input
                  disabled
                  type="text"
                  id="locationComponent"
                  value={getLabelByValue(
                    data.locationComponent,
                    LocationComponent
                  )}
                  name="locationComponent"
                  className="problem-details-field"
                />
              </div>
              <div>
                <label htmlFor="dma" className="problem-details-label">
                  DMA zona
                </label>
                <input
                  disabled
                  type="text"
                  id="dma"
                  value={getLabelByValue(data.dma, DMA)}
                  name="dma"
                  className="problem-details-field problem-details-last-field"
                />
              </div>
            </div>

            <div>
              <div className="form-field-container problem-details-mobile">
                <label
                  htmlFor="image"
                  id="image"
                  className="problem-details-label"
                >
                  Fotografija kvara
                </label>

                {!data.issue?.image && !data.image ? (
                  <div className="problem-details-no-image-text">
                    Fotografija nije učitana
                  </div>
                ) : (
                  <button
                    className="problem-details-show-image-button"
                    onClick={() => setShowImage(true)}
                  >
                    Pogledaj fotografiju
                  </button>
                )}

                {showImage && (
                  <ImageModal
                    image={data.image ? data.image : data.issue?.image}
                    title={'Fotografija kvara'}
                    folder={data.image ? 'problems' : 'issues'}
                    closeModal={() => setShowImage(false)}
                  />
                )}
              </div>

              {showImage && (
                <ImageModal
                  image={data.image ? data.image : data.issue?.image}
                  title={'Fotografija kvara'}
                  folder={data.image ? 'problems' : 'issues'}
                  closeModal={() => setShowImage(false)}
                />
              )}
              <div className="form-field-container">
                <label htmlFor="description" className="problem-details-label">
                  Opis kvara
                </label>
                <textarea
                  disabled
                  id="description"
                  value={data.description}
                  name="description"
                  maxLength="180"
                  className="problem-details-field problem-details-textarea"
                />
              </div>

              <div className="form-field-container">
                <label htmlFor="suggestion" className="problem-details-label">
                  Prijedlog rješenja
                </label>
                <textarea
                  disabled
                  id="suggestion"
                  value={data.suggestion}
                  name="suggestion"
                  className="problem-details-field problem-details-textarea"
                />
              </div>

              {!appInformation.statistics.problemModalActive &&
                [0, 8].includes(userDetails.role) &&
                data.status === 0 && (
                  <button
                    className="create-work-order-button problem-details-mobile"
                    onClick={() => setFormStep(1)}
                  >
                    Otvori radni nalog
                  </button>
                )}

              {!appInformation.statistics.problemModalActive &&
                ![0, 8].includes(userDetails.role) &&
                data.status === 0 && (
                  <div
                    className="problem-status-info problem-details-mobile "
                    style={{
                      borderColor: '#FFDE21',
                      color: '#FFDE21',
                    }}
                  >
                    Kvar u obradi
                  </div>
                )}
              {data.status === 1 && (
                <button
                  className="show-work-order-button problem-details-mobile"
                  onClick={() => setFormStep(3)}
                >
                  Radni nalog
                </button>
              )}
              {data.status === 2 && (
                <div
                  className="crd-status-info problem-details-mobile"
                  style={{
                    borderColor: '#21538f',
                    color: '#21538f',
                    marginTop: '18px',
                    textAlign: 'center',
                  }}
                >
                  Kvar popravljen
                </div>
              )}
            </div>
          </div>
          {!appInformation.statistics.problemModalActive &&
            [0, 8].includes(userDetails.role) &&
            data.status === 0 && (
              <button
                className="create-work-order-button problem-details-desktop"
                onClick={() => setFormStep(1)}
              >
                Otvori radni nalog
              </button>
            )}

          {!appInformation.statistics.problemModalActive &&
            ![0, 8].includes(userDetails.role) &&
            data.status === 0 && (
              <div
                className="problem-status-info problem-details-desktop "
                style={{
                  borderColor: '#FFDE21',
                  color: '#FFDE21',
                }}
              >
                Kvar u obradi
              </div>
            )}
          {!appInformation.statistics.problemModalActive &&
            data.status === 1 && (
              <button
                className="show-work-order-button problem-details-desktop"
                onClick={() => setFormStep(3)}
              >
                Radni nalog
              </button>
            )}
          {!appInformation.statistics.problemModalActive &&
            data.status === 2 && (
              <div
                className="crd-status-info problem-details-desktop"
                style={{
                  borderColor: '#21538f',
                  color: '#21538f',
                  marginTop: '18px',
                  textAlign: 'center',
                }}
              >
                Kvar popravljen
              </div>
            )}
        </div>
      )}
    </>
  );
}
